import React, { useState, useEffect } from 'react';
import { 
  Settings, Eye, EyeOff, Save, AlertCircle, Check, Download, 
  Trash2, Globe, Database, Lock, Moon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { settingsService, configService } from '../services';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { PageHeader } from '../components/shared';

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [models, setModels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  
  // Settings state
  const [storageMode, setStorageMode] = useState('ephemeral');
  const [modelProvider, setModelProvider] = useState('');
  const [locale, setLocale] = useState('en');
  
  // Data controls
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, modelsData] = await Promise.all([
        settingsService.get(),
        configService.getModels()
      ]);
      setSettings(settingsData);
      setModels(modelsData);
      setStorageMode(settingsData.storage_mode || 'ephemeral');
      setModelProvider(settingsData.model_provider_preference || '');
      setLocale(settingsData.locale || 'en');
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    
    try {
      await settingsService.update({ 
        storage_mode: storageMode,
        model_provider_preference: modelProvider || null,
        locale: locale
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await settingsService.export({ 
        include_analyses: true, 
        include_cases: true,
        format: 'json'
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `msml-health-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportDialogOpen(false);
    } catch (err) {
      setError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (type) => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      const request = {
        delete_analyses: type === 'all' || type === 'analyses',
        delete_cases: type === 'all' || type === 'cases',
        delete_account: type === 'account',
        confirmation: 'DELETE'
      };
      
      await settingsService.deleteData(request);
      
      if (type === 'account') {
        await logout();
      } else {
        setDeleteDialogOpen(false);
        setDeleteConfirmation('');
      }
    } catch (err) {
      setError('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout activeNav="settings">
        <div className="flex items-center justify-center h-full">
          <span className="msml-spinner" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeNav="settings">
      <div className="p-4 lg:p-6">
        <div className="max-w-3xl mx-auto">
          <PageHeader
            icon={<Settings />}
            title="Settings"
            subtitle="Manage your preferences, storage mode, language, and data controls."
          />

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/20 mb-6">
              <AlertCircle className="w-4 h-4 text-[#EF4444]" />
              <span className="text-sm text-[#EF4444]">{error}</span>
            </div>
          )}

          {saved && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-[#10B981]/10 border border-[#10B981]/20 mb-6">
              <Check className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">Settings saved successfully</span>
            </div>
          )}

          {/* Account Section */}
          <div className="msml-card mb-6" data-testid="account-settings">
            <h2 className="text-lg font-medium text-ink mb-4">Account</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <div>
                  <p className="text-sm text-ink">Name</p>
                  <p className="text-xs text-ink-muted">{user?.name}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <div>
                  <p className="text-sm text-ink">Email</p>
                  <p className="text-xs text-ink-muted">{user?.email}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between py-2 border-b border-white/10">
                <div>
                  <p className="text-sm text-ink">Account Type</p>
                  <p className="text-xs text-ink-muted capitalize">{settings?.account_type || 'free'}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-ink">Role</p>
                  <p className="text-xs text-ink-muted capitalize">{user?.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="msml-card mb-6" data-testid="privacy-settings">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-4 h-4 text-[#2E79FF]" />
              <h2 className="text-lg font-medium text-ink">Privacy</h2>
            </div>
            
            <div className="space-y-6">
              {/* Storage Mode */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {storageMode === 'ephemeral' ? (
                    <EyeOff className="w-5 h-5 text-ink-muted" />
                  ) : (
                    <Eye className="w-5 h-5 text-[#2E79FF]" />
                  )}
                  <div>
                    <p className="text-sm text-ink">Default Storage Mode</p>
                    <p className="text-xs text-ink-muted">
                      {storageMode === 'ephemeral' 
                        ? 'Analyses are not saved by default (privacy-first)' 
                        : 'Analyses are saved to your history'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-ink-muted">Ephemeral</span>
                  <Switch
                    checked={storageMode === 'persisted'}
                    onCheckedChange={(checked) => setStorageMode(checked ? 'persisted' : 'ephemeral')}
                    data-testid="storage-mode-switch"
                  />
                  <span className="text-xs text-ink-muted">Saved</span>
                </div>
              </div>
              
              <div className="p-3 bg-white/5 rounded-md">
                <p className="text-xs text-ink-muted">
                  <strong className="text-ink">Privacy-first design:</strong> In ephemeral mode, your text is analyzed and immediately discarded. 
                  It is never used to train models. You can override this per-session in the Analyze workspace.
                </p>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="msml-card mb-6" data-testid="preferences-settings">
            <h2 className="text-lg font-medium text-ink mb-4">Preferences</h2>
            
            <div className="space-y-4">
              {/* Model Provider */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-ink">Preferred Model Provider</p>
                  <p className="text-xs text-ink-muted">Default provider for analysis</p>
                </div>
                <Select value={modelProvider || 'auto'} onValueChange={(v) => setModelProvider(v === 'auto' ? '' : v)}>
                  <SelectTrigger className="w-40 msml-input">
                    <SelectValue placeholder="Auto" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1014] border-white/10">
                    <SelectItem value="auto">Auto (default)</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                    <SelectItem value="gemini">Gemini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Locale */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-ink-muted" />
                  <div>
                    <p className="text-sm text-ink">Language</p>
                    <p className="text-xs text-ink-muted">Interface language</p>
                  </div>
                </div>
                <Select value={locale} onValueChange={setLocale}>
                  <SelectTrigger className="w-40 msml-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1014] border-white/10">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Theme (placeholder) */}
              <div className="flex items-center justify-between opacity-50">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-ink-muted" />
                  <div>
                    <p className="text-sm text-ink">Theme</p>
                    <p className="text-xs text-ink-muted">Dark mode only (for now)</p>
                  </div>
                </div>
                <span className="text-xs text-ink-muted px-2 py-1 bg-white/5 rounded">Coming soon</span>
              </div>
            </div>
          </div>

          {/* Data Controls */}
          <div className="msml-card mb-6" data-testid="data-controls">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-4 h-4 text-[#2E79FF]" />
              <h2 className="text-lg font-medium text-ink">Data Controls</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm text-ink">Export My Data</p>
                  <p className="text-xs text-ink-muted">Download all your analyses and cases</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="msml-btn-secondary"
                  onClick={() => setExportDialogOpen(true)}
                  data-testid="export-data-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
              
              <div className="flex items-center justify-between py-2 border-t border-white/10">
                <div>
                  <p className="text-sm text-[#EF4444]">Delete My Data</p>
                  <p className="text-xs text-ink-muted">Permanently delete analyses, cases, or account</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-data-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="msml-btn-primary"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <span className="msml-spinner mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="msml-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ink">Export Your Data</DialogTitle>
            <DialogDescription className="text-ink-muted">
              Download a copy of all your data including analyses and cases.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-[#A1A1AA]">
              This will export:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-ink-muted">
              <li>• Your account information</li>
              <li>• All saved analyses</li>
              <li>• All cases</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="msml-btn-primary"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Download JSON'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="msml-card border-[#EF4444]/30 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444]">Delete Data</DialogTitle>
            <DialogDescription className="text-ink-muted">
              This action cannot be undone. Please choose what to delete.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Type DELETE to confirm</Label>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                className="msml-input"
                data-testid="delete-confirmation-input"
              />
            </div>
            
            <div className="space-y-2">
              <Button 
                variant="outline"
                className="w-full border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10"
                onClick={() => handleDelete('analyses')}
                disabled={deleting || deleteConfirmation !== 'DELETE'}
              >
                Delete All Analyses
              </Button>
              <Button 
                variant="outline"
                className="w-full border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10"
                onClick={() => handleDelete('cases')}
                disabled={deleting || deleteConfirmation !== 'DELETE'}
              >
                Delete All Cases
              </Button>
              <Button 
                variant="outline"
                className="w-full border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                onClick={() => handleDelete('account')}
                disabled={deleting || deleteConfirmation !== 'DELETE'}
              >
                Delete Account & All Data
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteConfirmation('');
            }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
